/**
 * Artifact Detector — analyzes tool call results and generates visual artifacts.
 * These artifacts are rendered inline in chat messages.
 */

export type ArtifactType =
  | 'issue_table'
  | 'issue_detail'
  | 'sprint_summary'
  | 'board_overview'
  | 'project_card'
  | 'spaces_list'
  | 'pages_list'
  | 'issue_distribution'
  | 'gantt_chart'
  | 'burndown_chart'
export interface ChatArtifact {
  id: string
  type: ArtifactType
  title: string
  data: any
  toolName: string
}
interface ToolResult {
  toolName: string
  toolResultData: any
  success: boolean
}

/**
 * Maps tool names to artifact types and extracts relevant data.
 */
const TOOL_ARTIFACT_MAP: Record<
  string,
  {
    type: ArtifactType
    titleFn: (data: any) => string
    dataExtractor: (data: any) => any
    minItems?: number // For arrays, minimum items to show artifact
  }
> = {
  // Jira - Issues
  'search-issues': {
    type: 'issue_table',
    titleFn: (data) =>
      `${data?.total || data?.issues?.length || 0} Issues encontrados`,
    dataExtractor: (data) => data?.issues || data || [],
    minItems: 1,
  },
  'agile-get-sprint-issues': {
    type: 'issue_table',
    titleFn: (data) =>
      `${data?.total || data?.issues?.length || 0} Issues del Sprint`,
    dataExtractor: (data) => data?.issues || data || [],
    minItems: 1,
  },
  'get-issue': {
    type: 'issue_detail',
    titleFn: (data) => data?.key || 'Issue',
    dataExtractor: (data) => data,
  },
  // Jira - Sprints
  'agile-get-sprints': {
    type: 'sprint_summary',
    titleFn: (data) => `${data?.values?.length || data?.length || 0} Sprints`,
    dataExtractor: (data) => data?.values || data || [],
    minItems: 1,
  },
  'agile-get-sprint': {
    type: 'sprint_summary',
    titleFn: (data) => data?.name || 'Sprint',
    dataExtractor: (data) => data,
  },
  // Jira - Boards
  'agile-get-boards': {
    type: 'board_overview',
    titleFn: (data) => `${data?.values?.length || data?.length || 0} Boards`,
    dataExtractor: (data) => data?.values || data || [],
    minItems: 1,
  },
  // Jira - Projects
  'get-projects': {
    type: 'project_card',
    titleFn: (data) => `${data?.length || 0} Proyectos`,
    dataExtractor: (data) => data || [],
    minItems: 1,
  },
  'get-project': {
    type: 'project_card',
    titleFn: (data) => data?.name || data?.key || 'Proyecto',
    dataExtractor: (data) => data,
  },
  // Confluence - Spaces
  confluence_get_spaces: {
    type: 'spaces_list',
    titleFn: (data) => `${data?.results?.length || data?.length || 0} Espacios`,
    dataExtractor: (data) => data?.results || data || [],
    minItems: 1,
  },
  confluence_get_space: {
    type: 'spaces_list',
    titleFn: (data) => data?.name || 'Espacio',
    dataExtractor: (data) => [data],
  },
  // Confluence - Pages
  confluence_get_pages_in_space: {
    type: 'pages_list',
    titleFn: (data) => `${data?.results?.length || data?.length || 0} Páginas`,
    dataExtractor: (data) => data?.results || data || [],
    minItems: 1,
  },
  confluence_search_content: {
    type: 'pages_list',
    titleFn: (data) =>
      `${data?.results?.length || data?.length || 0} Resultados`,
    dataExtractor: (data) => data?.results || data || [],
    minItems: 1,
  },
  confluence_get_page: {
    type: 'pages_list',
    titleFn: (data) => data?.title || 'Página',
    dataExtractor: (data) => [data],
  },
}

/**
 * Tool calls that are typically intermediate/infrastructure steps.
 * These only generate artifacts if they are the FINAL tool call
 * (i.e., the agent is directly presenting this data to the user).
 */
const INFRASTRUCTURE_TOOLS = new Set([
  'agile-get-boards',
  'get-boards',
  'get-projects',
  'agile-get-sprints',
  'get-sprints',
  'get-project',
])

/**
 * Check if the agent's response indicates it could NOT resolve the question.
 * If true, we suppress all artifacts to avoid showing irrelevant intermediate data.
 */
function isFailureResponse(responseText?: string): boolean {
  if (!responseText) return false
  const lower = responseText.toLowerCase()
  const failurePatterns = [
    'no pude',
    'no puedo',
    'no encontré',
    'no se encontró',
    'no se encontraron',
    'no fue posible',
    'no tengo acceso',
    'no hay datos',
    'no logré',
    'error al',
    'falló',
    'no está configurad',
    'no está conectad',
    'credenciales',
    'autenticación fallida',
    'token expirado',
    'sin resultados',
    'no hay resultados',
    'no se pudo conectar',
    'verificá las credenciales',
    'configurá',
    'no hay sprints',
    'no hay boards',
    'no hay proyectos',
  ]
  return failurePatterns.some((p) => lower.includes(p))
}

/**
 * Detect and generate artifacts from tool call results.
 * Returns an array of artifacts to render in the chat message.
 *
 * @param toolResults - All tool results from the agent loop
 * @param responseText - The agent's final response text (optional). Used to suppress
 *   artifacts when the agent couldn't resolve the question.
 */
export function detectArtifacts(
  toolResults: ToolResult[],
  responseText?: string,
): ChatArtifact[] {
  if (!Array.isArray(toolResults)) return []

  // If the agent's response indicates failure, don't show any artifacts
  if (isFailureResponse(responseText)) {
    console.log(
      '[ArtifactDetector] Suppressing artifacts — agent response indicates failure',
    )
    return []
  }

  // Determine which tools are "infrastructure" (intermediate steps)
  // Infrastructure tools only show artifacts if they are the LAST tool called
  // (meaning the agent is directly presenting that data)
  const lastToolName =
    toolResults.length > 0
      ? toolResults[toolResults.length - 1]?.toolName
      : null
  const hasFollowUpTools = toolResults.some(
    (r) => !INFRASTRUCTURE_TOOLS.has(r.toolName),
  )
  const artifacts: ChatArtifact[] = []
  for (const result of toolResults) {
    if (!result || !result.success || !result.toolResultData) continue
    const mapping = TOOL_ARTIFACT_MAP[result.toolName]
    if (!mapping) continue

    // Skip infrastructure tools if there are follow-up specific tools
    // (e.g., get-boards was just used to find board_id for get-sprint-issues)
    if (
      INFRASTRUCTURE_TOOLS.has(result.toolName) &&
      hasFollowUpTools &&
      result.toolName !== lastToolName
    ) {
      continue
    }
    try {
      const extractedData = mapping.dataExtractor(result.toolResultData)

      // Skip if array doesn't meet minimum items
      if (mapping.minItems !== undefined) {
        const items = Array.isArray(extractedData)
          ? extractedData
          : extractedData
            ? [extractedData]
            : []
        if (items.length < mapping.minItems) continue
      }

      // Skip if data is empty/null
      if (
        !extractedData ||
        (Array.isArray(extractedData) && extractedData.length === 0)
      ) {
        continue
      }
      artifacts.push({
        id: `artifact-${result.toolName}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: mapping.type,
        title: mapping.titleFn(result.toolResultData),
        data: extractedData,
        toolName: result.toolName,
      })
    } catch (e) {
      console.warn(
        `[ArtifactDetector] Failed to extract artifact from ${result.toolName}:`,
        e,
      )
    }
  }

  // ═══════════════════════════════════════════════
  // DEDUP: If both search-issues AND sprint-issues exist, keep only the sprint one
  // (search-issues was likely an intermediate step to get sprint data)
  // ═══════════════════════════════════════════════
  const issueTables = artifacts.filter((a) => a.type === 'issue_table')
  if (issueTables.length > 1) {
    const hasSprintTable = issueTables.some(
      (a) => a.toolName === 'agile-get-sprint-issues',
    )
    if (hasSprintTable) {
      // Remove non-sprint issue tables (they were intermediate)
      const toRemove = issueTables
        .filter((a) => a.toolName !== 'agile-get-sprint-issues')
        .map((a) => a.id)
      for (let i = artifacts.length - 1; i >= 0; i--) {
        if (toRemove.includes(artifacts[i].id)) {
          artifacts.splice(i, 1)
        }
      }
    }
  }

  // NOTE: Distribution chart is NOT auto-generated.
  // It only appears when the agent's response explicitly mentions distribution/distribución
  // or when the user asked for it.
  const remainingIssueTables = artifacts.filter((a) => a.type === 'issue_table')
  if (remainingIssueTables.length > 0 && responseText) {
    const lower = responseText.toLowerCase()
    const wantsDistribution =
      lower.includes('distribución') ||
      lower.includes('distribucion') ||
      lower.includes('distribution') ||
      lower.includes('por estado') ||
      lower.includes('by status') ||
      lower.includes('gráfico') ||
      lower.includes('chart')
    if (wantsDistribution) {
      const allIssues = remainingIssueTables.flatMap((a) =>
        Array.isArray(a.data) ? a.data : [a.data],
      )
      if (allIssues.length >= 3) {
        artifacts.push({
          id: `artifact-distribution-${Date.now()}`,
          type: 'issue_distribution',
          title: 'Distribución de Issues',
          data: allIssues,
          toolName: 'computed',
        })
      }
    }
  }

  // ═══════════════════════════════════════════════
  // GANTT CHART — auto-generate from sprints or epics with dates
  // ═══════════════════════════════════════════════

  // Gantt from sprints (get-sprints returns items with startDate/endDate)
  const sprintArtifacts = artifacts.filter(
    (a) =>
      a.type === 'sprint_summary' &&
      (a.toolName === 'agile-get-sprints' || a.toolName === 'get-sprints'),
  )
  for (const sprintArtifact of sprintArtifacts) {
    const sprints = Array.isArray(sprintArtifact.data)
      ? sprintArtifact.data
      : [sprintArtifact.data]
    const sprintsWithDates = sprints.filter(
      (s: any) => s?.startDate && s?.endDate,
    )
    if (sprintsWithDates.length >= 2) {
      artifacts.push({
        id: `artifact-gantt-sprints-${Date.now()}`,
        type: 'gantt_chart',
        title: `Timeline de ${sprintsWithDates.length} Sprints`,
        data: sprintsWithDates.map((s: any) => ({
          id: s.id,
          name: s.name || `Sprint ${s.id}`,
          startDate: s.startDate,
          endDate: s.endDate,
          state: s.state || s.status || 'active',
          goal: s.goal || '',
        })),
        toolName: 'computed',
      })
    }
  }

  // Gantt from epics (search-issues may return epics with dates)
  const issueArtifacts = artifacts.filter((a) => a.type === 'issue_table')
  for (const issueArtifact of issueArtifacts) {
    const issues = Array.isArray(issueArtifact.data) ? issueArtifact.data : []
    const itemsWithDates = issues.filter((issue: any) => {
      const fields = issue.fields || issue
      const startDate =
        fields.customfield_10015 || fields.startDate || issue.startDate
      const endDate =
        fields.duedate || fields.endDate || issue.endDate || issue.dueDate
      return startDate && endDate
    })
    if (itemsWithDates.length >= 2) {
      artifacts.push({
        id: `artifact-gantt-issues-${Date.now()}`,
        type: 'gantt_chart',
        title: `Timeline de ${itemsWithDates.length} Items`,
        data: itemsWithDates.map((issue: any) => {
          const fields = issue.fields || issue
          return {
            id: issue.key || issue.id,
            name: `${issue.key || ''} ${fields.summary || issue.summary || 'Item'}`.trim(),
            startDate:
              fields.customfield_10015 || fields.startDate || issue.startDate,
            endDate:
              fields.duedate ||
              fields.endDate ||
              issue.endDate ||
              issue.dueDate,
            state: fields.status?.name || issue.status || 'To Do',
            goal: '',
          }
        }),
        toolName: 'computed',
      })
    }
  }
  // ═══════════════════════════════════════════════
  // BURNDOWN CHART — auto-generate when user asks for burndown/burnup
  // Needs sprint issues + sprint dates
  // ═══════════════════════════════════════════════
  if (responseText) {
    const lowerResp = responseText.toLowerCase()
    const wantsBurndown =
      lowerResp.includes('burndown') ||
      lowerResp.includes('burnup') ||
      lowerResp.includes('burn down') ||
      lowerResp.includes('burn up')

    if (wantsBurndown) {
      // Find sprint data (dates) from tool results
      let sprintInfo: {
        name: string
        startDate: string
        endDate: string
      } | null = null
      for (const result of toolResults) {
        if (!result.success || !result.toolResultData) continue
        if (
          result.toolName === 'agile-get-sprints' ||
          result.toolName === 'agile-get-active-sprints' ||
          result.toolName === 'agile-get-sprint'
        ) {
          const data = result.toolResultData
          // Could be array (get-sprints) or single (get-sprint)
          const sprints = data?.values || (Array.isArray(data) ? data : [data])
          // Pick active sprint, or first with dates
          const activeSprint =
            sprints.find(
              (s: any) => s?.state === 'active' && s?.startDate && s?.endDate,
            ) || sprints.find((s: any) => s?.startDate && s?.endDate)
          if (activeSprint) {
            sprintInfo = {
              name: activeSprint.name || 'Sprint',
              startDate: activeSprint.startDate,
              endDate: activeSprint.endDate,
            }
          }
        }
      }

      // Find sprint issues
      let sprintIssues: any[] = []
      for (const result of toolResults) {
        if (!result.success || !result.toolResultData) continue
        if (result.toolName === 'agile-get-sprint-issues') {
          sprintIssues =
            result.toolResultData?.issues || result.toolResultData || []
        }
      }
      // Fallback: use search-issues if no sprint-issues
      if (sprintIssues.length === 0) {
        for (const result of toolResults) {
          if (!result.success || !result.toolResultData) continue
          if (result.toolName === 'search-issues') {
            sprintIssues =
              result.toolResultData?.issues || result.toolResultData || []
          }
        }
      }

      if (sprintIssues.length > 0) {
        artifacts.push({
          id: `artifact-burndown-${Date.now()}`,
          type: 'burndown_chart',
          title: sprintInfo
            ? `Burndown — ${sprintInfo.name}`
            : 'Burndown Chart',
          data: {
            sprint: sprintInfo,
            issues: sprintIssues,
          },
          toolName: 'computed',
        })
      }
    }
  }

  return artifacts
}

/**
 * Check if a tool result should generate an artifact.
 */
export function shouldGenerateArtifact(toolName: string): boolean {
  return toolName in TOOL_ARTIFACT_MAP
}
